import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  RocketLaunch as RocketIcon
} from '@mui/icons-material';

interface EmailCaptureModalProps {
  open: boolean;
  onClose: (email: string, name: string) => void;
}

const EmailCaptureModal: React.FC<EmailCaptureModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = () => {
    let valid = true;

    // Validate name
    if (!name) {
      setNameError('Name is required');
      valid = false;
      console.log('Name validation failed');
    } else {
      setNameError('');
    }

    // Validate email
    if (!email) {
      setEmailError('Email is required');
      valid = false;
      console.log('Email validation failed: empty');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Invalid email address');
      valid = false;
      console.log('Email validation failed: invalid format');
    } else {
      setEmailError('');
    }

    // Validate password
    if (password !== 'oppiesgetbusy') {
      setPasswordError('Incorrect access code');
      valid = false;
      console.log('Password validation failed');
    } else {
      setPasswordError('');
    }

    if (valid) {
      console.log('Validation passed, submitting form');
      onClose(email, name);
    } else {
      console.log('Validation failed, not submitting form');
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.5)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <RocketIcon sx={{ fontSize: 40, color: theme.palette.secondary.main, mr: 2 }} />
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 'bold',
            }}
          >
            Welcome to Oppy's Investor Deck
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, mb: 3 }}
        >
          Please enter your information to access the presentation
        </Typography>

        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <TextField
            autoFocus
            margin="normal"
            id="name"
            label="Your Name"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!nameError}
            helperText={nameError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="normal"
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="normal"
            id="password"
            label="Access Code"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError || 'Contact ag@oppy.pro for access'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Button
            onClick={handleSubmit}
            variant="contained"
            fullWidth
            size="large"
            sx={{
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: 'bold',
              py: 1.5,
              fontSize: '1.1rem',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
              }
            }}
          >
            Access Investor Deck
          </Button>
        </Box>

        <Box textAlign="center" mt={3}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Having trouble? Email us at{' '}
            <Box
              component="span"
              sx={{
                color: theme.palette.secondary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.secondary.dark,
                }
              }}
              onClick={() => window.open('mailto:ag@oppy.pro', '_blank')}
            >
              ag@oppy.pro
            </Box>
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EmailCaptureModal;